import React, { useState, useEffect, useRef } from 'react';

const HomeVenueSelector = ({ value, onChange, placeholder = "Search for your home venue..." }) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [loading, setLoading] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Initialize Google Places services using legacy API
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
    }
  }, []);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  const handleInputChange = (inputValue) => {
    setSearchTerm(inputValue);
    onChange(inputValue); // Update the form value immediately
    
    if (!inputValue.trim() || !autocompleteService.current) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    setLoading(true);
    
    // Detect if user is searching for a city/zip code or a venue
    const isZipCode = /^\d{5}(-\d{4})?$/.test(inputValue.trim());
    const isCityState = /^[a-zA-Z\s]+,\s*[A-Z]{2}$/.test(inputValue.trim());
    const isCitySearch = inputValue.toLowerCase().includes('atlanta') || inputValue.toLowerCase().includes('ga') || inputValue.toLowerCase().includes('georgia');
    
    let request;
    if (isZipCode || isCityState || isCitySearch) {
      // For zip codes, cities, and state searches, use geocode only
      request = {
        input: inputValue,
        types: ['geocode'],
        componentRestrictions: { country: 'us' }
      };
    } else {
      // For venue searches, use establishment only
      request = {
        input: inputValue,
        types: ['establishment'],
        componentRestrictions: { country: 'us' }
      };
    }

    autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
      setLoading(false);
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setPredictions(predictions);
        setShowPredictions(true);
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    });
  };

  const handlePredictionSelect = (prediction) => {
    if (!placesService.current) return;

    placesService.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['name', 'formatted_address', 'geometry', 'place_id', 'types', 'website', 'formatted_phone_number']
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const venueName = place.name;
          setSearchTerm(venueName);
          onChange(venueName);
          setShowPredictions(false);
          setPredictions([]);
        }
      }
    );
  };

  const handleInputFocus = () => {
    if (predictions.length > 0) {
      setShowPredictions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding predictions to allow for clicks
    setTimeout(() => {
      setShowPredictions(false);
    }, 200);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none pr-10"
        />
        
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
          </div>
        )}
      </div>

      {/* Predictions Dropdown */}
      {showPredictions && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              onClick={() => handlePredictionSelect(prediction)}
              className="w-full text-left p-3 hover:bg-gray-700 text-white border-b border-gray-600 last:border-b-0 transition-colors"
            >
              <div className="font-medium">{prediction.structured_formatting.main_text}</div>
              <div className="text-sm text-gray-400">{prediction.structured_formatting.secondary_text}</div>
            </button>
          ))}
        </div>
      )}

      {/* No Results */}
      {showPredictions && predictions.length === 0 && searchTerm.trim() && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-3">
          <div className="text-gray-400 text-center">No venues found</div>
        </div>
      )}
    </div>
  );
};

export default HomeVenueSelector; 